import React from "react";
import { alpha, useTheme } from "@mui/material/styles";
import {
  Box,
  Typography,
  Drawer,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { useTranslation } from "react-i18next";
import * as Icons from "@mui/icons-material";

const ProjectDrawer = ({
  open,
  onClose,
  editProyectoId,
  newProyecto,
  marcas,
  temporadas,
  estados,
  handleProyectoChange,
  handleProyectoSubmit,
  formatNumberWithDots,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const accent = "#ffac7e";

  // Render icono genérico (temporadas + estados)
  const getIcon = (nombreIcono, color) => {
    const IconComponent = Icons[nombreIcono];
    if (!IconComponent) return null;
    return <IconComponent sx={{ mr: 1, color }} />;
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: 470,
          maxWidth: "100%",
          borderTopLeftRadius: 24,
          borderBottomLeftRadius: 24,
          borderLeft: `1px solid ${alpha(accent, 0.12)}`,
          background: `linear-gradient(180deg, ${alpha(
            theme.palette.common.white,
            0.96,
          )} 0%, ${alpha(theme.palette.background.paper, 0.98)} 100%)`,
        },
      }}
    >
      <Box sx={{ p: 1.4, borderBottom: `1px solid ${alpha(accent, 0.1)}` }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 1,
          }}
        >
          <Box>
            <Typography
              sx={{
                fontSize: "0.7rem",
                fontWeight: 800,
                letterSpacing: 0.8,
                textTransform: "uppercase",
                color: "text.secondary",
              }}
            >
              CRM
            </Typography>
            <Typography sx={{ fontWeight: 900, fontSize: "1.35rem", lineHeight: 1 }}>
              {editProyectoId
                ? t("Projects.EditarProyecto")
                : t("Projects.AniadirProyecto")}
            </Typography>
            <Typography sx={{ mt: 0.35, fontSize: "0.8rem", color: "text.secondary" }}>
              Contexto, estado y previsiones del proyecto
            </Typography>
          </Box>
          <IconButton
            onClick={onClose}
            sx={{
              border: "1px solid",
              borderColor: alpha(accent, 0.12),
              backgroundColor: alpha(theme.palette.common.white, 0.76),
            }}
          >
            <CloseRoundedIcon />
          </IconButton>
        </Box>
      </Box>

      <Box
        sx={{
          p: 1.5,
          display: "grid",
          gap: 1,
          "& .MuiOutlinedInput-root": {
            borderRadius: 3,
            backgroundColor: alpha(theme.palette.common.white, 0.82),
            boxShadow: `0 8px 18px ${alpha(theme.palette.common.black, 0.04)}`,
            "& fieldset": {
              borderColor: alpha(accent, 0.12),
            },
          },
        }}
      >

        {/* Subject */}
        <TextField
          name="subject"
          label="Subject"
          value={newProyecto.subject}
          onChange={handleProyectoChange}
          fullWidth
          margin="dense"
          sx={{
            "& .MuiInputBase-input": {
              color: "#34afe2",
              fontWeight: 800,
              fontSize: 20,
            },
          }}
        />

        {/* Marca */}
        <FormControl fullWidth margin="dense">
          <InputLabel>Marca</InputLabel>
          <Select
            name="marcaId"
            value={newProyecto.marcaId || ""}
            onChange={handleProyectoChange}
          >
            {marcas.map((marca) => (
              <MenuItem key={marca.ID_MARCA} value={marca.ID_MARCA}>
                {marca.MARCA}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Temporada dinámica */}
        <FormControl fullWidth margin="dense">
          <InputLabel>{t("Projects.Season")}</InputLabel>
          <Select
            name="proyectType"
            value={newProyecto.proyectType || ""}
            onChange={handleProyectoChange}
            renderValue={(value) => {
              const temp = temporadas.find((t) => t.TEMPORADA === value);
              if (!temp) return value;
              return (
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  {getIcon(temp.ICONO, temp.COLOR_HEX)}
                  {temp.TEMPORADA}
                </Box>
              );
            }}
          >
            {temporadas
              .filter((t) => t.ID_TEMPORADA > 16)
              .map((temporada) => (
                <MenuItem
                  key={temporada.ID_TEMPORADA}
                  value={temporada.TEMPORADA}
                >
                  {getIcon(temporada.ICONO, temporada.COLOR_HEX)}
                  <span
                    style={{ marginLeft: ".5em", color: temporada.COLOR_HEX }}
                  >
                    {temporada.TEMPORADA}
                  </span>
                </MenuItem>
              ))}
          </Select>
        </FormControl>

        {/* Estado */}
        <FormControl fullWidth margin="dense">
          <InputLabel>{t("Projects.Status")}</InputLabel>
          <Select
            name="status"
            value={newProyecto.status}
            onChange={handleProyectoChange}
            renderValue={(selected) => {
              const est = estados.find((e) => e.NOMBRE === selected);
              if (!est) return selected;
              return (
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  {getIcon(est.ICONO, est.COLOR_HEX)}
                  {est.NOMBRE}
                </Box>
              );
            }}
          >
            {estados.map((estado) => (
              <MenuItem key={estado.ID_ESTADO} value={estado.NOMBRE}>
                {getIcon(estado.ICONO, estado.COLOR_HEX)}
                {estado.NOMBRE}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Notas */}
        <TextField
          name="notes"
          label={t("Projects.Notas")}
          value={newProyecto.notes}
          onChange={handleProyectoChange}
          fullWidth
          margin="dense"
          multiline
          rows={4}
        />

        {/* Ventas */}
        <Box sx={{ display: "flex", justifyContent: "space-between", mt: 2 }}>
          <TextField
            name="previsionVenta"
            label={t("Projects.PrevisionVenta")}
            value={formatNumberWithDots(newProyecto.previsionVenta)}
            onChange={handleProyectoChange}
            sx={{ width: "49%" }}
          />
          <TextField
            name="previsionPrendas"
            label={t("Projects.PrevisionPrendas")}
            value={formatNumberWithDots(newProyecto.previsionPrendas)}
            onChange={handleProyectoChange}
            sx={{ width: "49%" }}
          />
        </Box>

        {/* Final */}
        <Box sx={{ display: "flex", justifyContent: "space-between", mt: 2 }}>
          <TextField
            name="ventaFinal"
            label={t("Projects.VentaFinal")}
            value={formatNumberWithDots(newProyecto.ventaFinal)}
            onChange={handleProyectoChange}
            sx={{ width: "49%" }}
          />
          <TextField
            name="prendasFinal"
            label={t("Projects.VentaFinal")}
            value={formatNumberWithDots(newProyecto.prendasFinal)}
            onChange={handleProyectoChange}
            sx={{ width: "49%" }}
          />
        </Box>

        <Button
          onClick={handleProyectoSubmit}
          variant="contained"
          sx={{
            background: `linear-gradient(135deg, ${accent} 0%, ${alpha(
              accent,
              0.82,
            )} 100%)`,
            color: theme.palette.text.primary,
            mt: 2,
            width: "100%",
            fontWeight: 800,
            borderRadius: 999,
            py: 1.25,
            boxShadow: `0 14px 30px ${alpha(accent, 0.24)}`,
          }}
        >
          {editProyectoId
            ? t("Projects.Actualizar")
            : t("Projects.AniadirProyecto")}
        </Button>
      </Box>
    </Drawer>
  );
};

export default ProjectDrawer;
